import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { VotingControls } from './VotingControls'

interface Photo {
  id: string
  photoTitle: string
  name: string
  grade: string
  photoUrl: string
  thumbnailUrl?: string
  lightboxUrl?: string
}

interface Selections {
  first: string | null
  second: string | null
  third: string | null
}

interface PhotoLightboxProps {
  photos: Photo[]
  currentIndex: number
  isOpen: boolean
  onClose: () => void
  onNavigate: (index: number) => void
  selections: Selections
  onVote: (photoId: string, rank: 1 | 2 | 3) => void
  canSubmit?: boolean
  isSubmitting?: boolean
  onSubmit?: () => void
  onImageError?: () => void
}

export function PhotoLightbox({
  photos,
  currentIndex,
  isOpen,
  onClose,
  selections,
  onVote,
  canSubmit = false,
  isSubmitting = false,
  onSubmit,
  onImageError,
}: PhotoLightboxProps) {
  const currentPhoto = photos[currentIndex]

  // Keyboard: Escape to close, 1/2/3 keys to vote
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case '1':
          if (currentPhoto) onVote(currentPhoto.id, 1)
          break
        case '2':
          if (currentPhoto) onVote(currentPhoto.id, 2)
          break
        case '3':
          if (currentPhoto) onVote(currentPhoto.id, 3)
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, currentPhoto, onVote])

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!currentPhoto) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
          onClick={onClose}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Main content */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- stopPropagation guard for the backdrop-dismiss; keyboard users close via the Escape handler above or the close button */}
          <div
            className="flex w-full max-w-4xl flex-col items-center px-4 sm:px-16"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Photo container */}
            <div className="w-full rounded-2xl border border-white/10 bg-white/5 p-4">
              {/* Fixed-height image container */}
              <div className="relative flex h-[50vh] w-full items-center justify-center sm:h-[55vh]">
                <motion.img
                  key={currentPhoto.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  src={currentPhoto.lightboxUrl || currentPhoto.thumbnailUrl || currentPhoto.photoUrl}
                  alt={currentPhoto.photoTitle}
                  className="max-h-full max-w-full object-contain"
                  onError={onImageError}
                />
              </div>

              {/* Title */}
              <div className="mt-3 text-center">
                <h2 className="text-xl font-semibold text-white">{currentPhoto.photoTitle}</h2>
              </div>
            </div>

            {/* Voting controls */}
            {onSubmit && (
              <div className="mt-4 w-full rounded-xl border border-white/10 bg-white/5 p-4">
                <p className="mb-3 text-center text-xs text-white/60">
                  Tap 1st, 2nd, or 3rd to rank "
                  <span className="font-medium text-white">{currentPhoto.photoTitle}</span>"
                </p>
                <VotingControls
                  photos={photos}
                  selections={selections}
                  currentPhotoId={currentPhoto.id}
                  onVote={onVote}
                  onClearSelection={(rank) => {
                    const rankKey = rank === 1 ? 'first' : rank === 2 ? 'second' : 'third'
                    const selectedId = selections[rankKey]
                    if (selectedId) onVote(selectedId, rank)
                  }}
                  canSubmit={canSubmit}
                  isSubmitting={isSubmitting}
                  onSubmit={onSubmit}
                  variant="modal"
                />
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
